import { Box, Button, CircularProgress, Skeleton, Typography } from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import { isAxiosError } from "axios";
import { useCallback, useEffect, useMemo, useState } from "react";
import { getPaypalOnboarding } from "../../../../utils";
import { IPaypalMerchantInfo } from "../../../paypal/interfaces";
import { useQueryClient } from "@tanstack/react-query";
import { useGetMerchantInfo } from "../../../paypal/actions/useGetMerchantInfo";
import type { InstallationComponentProps } from "../../interfaces/appInstallation.interface";
import { useSocket } from "../../../../context/SocketContext";

type Severity = "success" | "error" | "warning" | "info";

const BUTTON_SX = {
  fontWeight: 700,
  textTransform: "none",
  fontSize: "1rem",
  py: 1.5,
  px: 3,
  borderRadius: 2,
  background: "linear-gradient(135deg, #FB8E00 0%, rgba(251, 142, 0, 0.85) 100%)",
  color: "white",
  boxShadow: "0 4px 12px rgba(251, 142, 0, 0.3)",
  "&:hover": {
    background: "linear-gradient(135deg, #e67e00 0%, rgba(230, 126, 0, 0.85) 100%)",
    boxShadow: "0 8px 20px rgba(251, 142, 0, 0.4)",
    transform: "translateY(-2px)",
  },
  transition: "all 0.3s ease",
} as const;

const getOnboardingActionUrl = (data: any): string | null => {
  const href = data?.links?.find((l: any) => l?.rel === "action_url")?.href;
  return typeof href === "string" && href.length ? href : null;
};

const getAxiosMessage = (err: unknown): { severity: Severity; message: string } => {
  if (!isAxiosError(err)) {
    return { severity: "error", message: "Internal server error" };
  }

  const status = err.response?.status;
  const apiMessage = err.response?.data?.message || "Failed to connect to PayPal";

  if (status === 400) return { severity: "warning", message: apiMessage };
  if (status === 401) return { severity: "error", message: "Unauthorized. Please sign in again." };
  if (status === 404) return { severity: "error", message: "Onboarding service not found." };

  return { severity: "error", message: apiMessage };
};

export const EcommercePaypalButtonSetup = ({ app }: InstallationComponentProps) => {
  const token = useMemo(() => sessionStorage.getItem("token") ?? "", []);

  const [loading, setLoading] = useState(false);
  const [waitingForConnection, setWaitingForConnection] = useState(false);

  const queryClient = useQueryClient();
  const { data: merchantInfoData, isLoading: merchantInfoLoading } = useGetMerchantInfo();
  const { socket } = useSocket();

  const isConnected = Boolean(merchantInfoData?.merchantId);
  const isBusy = loading;

  useEffect(() => {
    if (!socket) return;

    const onOnboardingComplete = (payload: IPaypalMerchantInfo | string) => {
      const data = typeof payload === "string" ? JSON.parse(payload) : payload;
      if (!data) return;
      setWaitingForConnection(false);
      setFeedback(null);
      queryClient.invalidateQueries({ queryKey: ["merchant-info"] });
      queryClient.invalidateQueries({ queryKey: ["appStatus", app.id] });
    };

    socket.on("paypalOnboardingCompleted", onOnboardingComplete);
    return () => {
      socket.off("paypalOnboardingCompleted", onOnboardingComplete);
    };
  }, [socket, queryClient, app.id]);

  const [feedback, setFeedback] = useState<{ severity: Severity; message: string } | null>(null);

  const handleConnectPaypal = useCallback(async () => {
    setLoading(true);
    setFeedback(null);

    try {
      const response = await getPaypalOnboarding(token);
      const actionUrl = getOnboardingActionUrl(response?.data);

      if (!actionUrl) {
        setFeedback({ severity: "error", message: "Onboarding URL was not found in the response." });
        setLoading(false);
        return;
      }

      window.open(actionUrl, "_blank", "noopener,noreferrer");
      setFeedback({ severity: "success", message: "Redirecting to PayPal to complete the setup..." });

      // Start waiting for socket connection
      setWaitingForConnection(true);
      setLoading(false);
    } catch (err) {
      setFeedback(getAxiosMessage(err));
      setLoading(false);
    }
  }, [token]);

  return (
    <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 1.5 }}>
      {merchantInfoLoading ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <Skeleton variant="rounded" width="100%" height={48} animation="wave" sx={{ borderRadius: 2 }} />
          <Box
            sx={{
              p: 1.5,
              borderRadius: 1.5,
              background: "linear-gradient(135deg, rgba(100, 116, 139, 0.05) 0%, rgba(100, 116, 139, 0.02) 100%)",
              border: "1px solid rgba(100, 116, 139, 0.1)",
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Skeleton variant="text" width="75%" height={20} animation="wave" sx={{ bgcolor: "rgba(100, 116, 139, 0.15)" }} />
              <Skeleton variant="text" width="55%" height={16} animation="wave" sx={{ bgcolor: "rgba(100, 116, 139, 0.15)" }} />
              <Skeleton variant="text" width="60%" height={16} animation="wave" sx={{ bgcolor: "rgba(100, 116, 139, 0.15)" }} />
            </Box>
          </Box>
        </Box>
      ) : (
        <>
          {!isConnected && (
            <Button
              variant="contained"
              size="large"
              onClick={isConnected ? undefined : handleConnectPaypal}
              disabled={isBusy || isConnected || waitingForConnection}
              fullWidth

              sx={{
                ...BUTTON_SX,
                ...(isConnected
                  ? {
                    background: "linear-gradient(135deg, #22c55e 0%, rgba(34, 197, 94, 0.85) 100%)",
                    boxShadow: "0 4px 12px rgba(34, 197, 94, 0.30)",
                    "&:hover": {
                      background: "linear-gradient(135deg, #16a34a 0%, rgba(22, 163, 74, 0.85) 100%)",
                      boxShadow: "0 8px 20px rgba(34, 197, 94, 0.40)",
                      transform: "translateY(-2px)",
                    },
                  }
                  : waitingForConnection
                    ? {
                      background: "linear-gradient(135deg, #64748b 0%, rgba(100, 116, 139, 0.85) 100%)",
                      boxShadow: "0 4px 12px rgba(100, 116, 139, 0.30)",
                      cursor: "wait",
                      "&:hover": {
                        background: "linear-gradient(135deg, #64748b 0%, rgba(100, 116, 139, 0.85) 100%)",
                        transform: "none",
                      },
                    }
                    : {}),
              }}
            >
              {isBusy || waitingForConnection ? <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} /> : null}
              {waitingForConnection ? "Waiting for connection..." : isBusy ? "Installing..." : "Install Now"}
            </Button>
          )}

          {/* Skeleton while waiting for connection */}
          <AnimatePresence mode="wait">
            {waitingForConnection && !isConnected ? (
              <motion.div
                key="skeleton"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 1.5,
                    background: "linear-gradient(135deg, rgba(100, 116, 139, 0.05) 0%, rgba(100, 116, 139, 0.02) 100%)",
                    border: "1px solid rgba(100, 116, 139, 0.1)",
                  }}
                >
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    <Skeleton variant="text" width="75%" height={20} animation="wave" sx={{ bgcolor: "rgba(100, 116, 139, 0.15)" }} />
                    <Skeleton variant="text" width="55%" height={16} animation="wave" sx={{ bgcolor: "rgba(100, 116, 139, 0.15)" }} />
                  </Box>
                </Box>
              </motion.div>
            ) : null}
          </AnimatePresence>
        </>
      )}

      {/* Feedback messages */}
      <AnimatePresence mode="wait">
        {feedback && !waitingForConnection ? (
          <motion.div
            key="feedback"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
          >
            <Box
              sx={{
                p: 1.5,
                borderRadius: 1.5,
                background:
                  feedback.severity === "success"
                    ? "linear-gradient(135deg, rgba(251, 142, 0, 0.1) 0%, rgba(251, 142, 0, 0.05) 100%)"
                    : "linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(239, 68, 68, 0.05) 100%)",
                border: feedback.severity === "success" ? "1px solid rgba(251, 142, 0, 0.2)" : "1px solid rgba(239, 68, 68, 0.2)",
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: feedback.severity === "success" ? "#FB8E00" : "#ef4444",
                  fontWeight: 500,
                  fontSize: "0.875rem",
                }}
              >
                {feedback.message}
              </Typography>
            </Box>
          </motion.div>
        ) : null}
      </AnimatePresence>

    </Box>
  );
};
